import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2, CornerDownLeft, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import useRecentSearches from "@/lib/useRecentSearches";
import SearchRecentSection from "@/components/SearchRecentSection";

import { log } from "@/lib/log";
const CACHE_MAX = 20;        // last N result-sets kept in memory
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min — short enough to pick up new content

/**
 * Global website search command palette.
 *
 * Trigger: a "Search" button (or "/" / "⌘K" keyboard shortcut from any page).
 * Backend: GET /api/search?q=...  returns { groups: { Pages: [...], Tools: [...], ... } }.
 *
 * Renders a modal overlay with a single input + grouped results. Arrow keys
 * navigate; Enter opens; Escape closes.
 */
export default function SearchCommand() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [data, setData] = useState({ groups: {}, items: [] });
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const { recent, push: pushRecent, clear: clearRecent } = useRecentSearches();
  const cacheRef = useRef(new Map());
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Global keyboard shortcut: "/" or ⌘K / Ctrl+K opens the palette
  useEffect(() => {
    const handler = (e) => {
      const k = e.key;
      const meta = e.metaKey || e.ctrlKey;
      // Don't hijack when user is typing in another input
      const inField = ["INPUT", "TEXTAREA", "SELECT"].includes(e.target?.tagName) || e.target?.isContentEditable;
      if ((meta && (k === "k" || k === "K")) || (k === "/" && !inField)) {
        e.preventDefault();
        setOpen(true);
      } else if (k === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Focus the input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ("");
      setData({ groups: {}, items: [] });
      setActiveIdx(0);
    }
  }, [open]);

  // Debounced search — backed by a client-side cache (last 20 queries × 5 min)
  // so re-running a recent search is instant and doesn't roundtrip the API.
  useEffect(() => {
    if (!open) return;
    const text = q.trim();
    if (text.length < 2) {
      setData({ groups: {}, items: [] });
      return;
    }
    // Cache hit?
    const cached = cacheRef.current.get(text);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setData(cached.data);
      setActiveIdx(0);
      setLoading(false);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/search", { params: { q: text } });
        const payload = r.data || { groups: {}, items: [] };
        setData(payload);
        setActiveIdx(0);
        // Trim cache to CACHE_MAX (LRU-ish — Map keeps insertion order)
        cacheRef.current.set(text, { data: payload, ts: Date.now() });
        if (cacheRef.current.size > CACHE_MAX) {
          const firstKey = cacheRef.current.keys().next().value;
          cacheRef.current.delete(firstKey);
        }
      } catch (err) {
        // soft fail — empty results
        log.warn("[SearchCommand] failed:", err?.message);
        setData({ groups: {}, items: [] });
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => clearTimeout(t);
  }, [q, open]);

  const choose = useCallback(
    (item) => {
      if (!item) return;
      pushRecent(q.trim() || item.title, item);
      setOpen(false);
      navigate(item.path);
    },
    [navigate, q, pushRecent]
  );

  const replayRecent = useCallback((r) => {
    if (r.path) {
      pushRecent(r.q, { path: r.path, title: r.title });
      setOpen(false);
      navigate(r.path);
    } else {
      setQ(r.q);
    }
  }, [navigate, pushRecent]);

  const onInputKey = (e) => {
    const items = data.items || [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(items[activeIdx]);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Search RiskPedia (press / or ⌘K)"
        className="hidden sm:inline-flex items-center gap-2 rounded-full border border-rp-border hover:border-orange-400 text-rp-textDim hover:text-rp-text px-3 py-1.5 text-xs font-mono transition-colors"
        data-testid="navbar-search-trigger"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden md:inline">Search</span>
        <kbd className="hidden md:inline ml-1 rounded border border-rp-border bg-rp-surface2 px-1.5 py-0.5 text-[10px] text-rp-textFaint">/</kbd>
      </button>

      {/* mobile icon-only button */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Search"
        className="sm:hidden inline-flex items-center justify-center rounded-full border border-rp-border text-rp-textDim p-1.5"
        data-testid="navbar-search-trigger-mobile"
      >
        <Search className="h-4 w-4" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
          onClick={() => setOpen(false)}
          data-testid="search-modal"
        >
          <div
            className="w-full max-w-2xl rounded-2xl border border-rp-border bg-rp-bg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-rp-border px-4 py-3">
              <Search className="h-4 w-4 text-rp-textDim shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Search frameworks, India regulators, tools, glossary…"
                className="flex-1 bg-transparent text-rp-text placeholder-rp-textFaint text-sm focus:outline-none"
                data-testid="search-input"
              />
              {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-rp-textFaint" />}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-rp-textFaint hover:text-rp-text"
                title="Close (Esc)"
                data-testid="search-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto" data-testid="search-results">
              {q.trim().length < 2 && recent.length === 0 && (
                <div className="p-8 text-center text-rp-textFaint text-sm">
                  Start typing to search frameworks, India regulators, tools, glossary terms and pages.
                </div>
              )}
              {q.trim().length < 2 && (
                <SearchRecentSection recent={recent} onReplay={replayRecent} onClear={clearRecent} />
              )}
              {q.trim().length >= 2 && !loading && (data.items || []).length === 0 && (
                <div className="p-8 text-center text-rp-textFaint text-sm" data-testid="search-empty">
                  No results for &ldquo;<span className="font-mono">{q}</span>&rdquo;.
                </div>
              )}
              {Object.entries(data.groups || {}).map(([groupName, hits]) => (
                <div key={groupName} className="border-b border-rp-border last:border-0" data-testid={`search-group-${groupName.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="px-4 py-2 text-[10px] font-mono uppercase tracking-[0.18em] text-rp-textFaint bg-rp-surface/50">
                    {groupName}
                  </div>
                  <ul>
                    {hits.map((h) => {
                      const idx = (data.items || []).indexOf(h);
                      const active = idx === activeIdx;
                      return (
                        <li key={h.path}>
                          <button
                            type="button"
                            onClick={() => choose(h)}
                            onMouseEnter={() => setActiveIdx(idx)}
                            className={`w-full text-left px-4 py-2.5 flex items-start gap-3 ${active ? "bg-orange-400/10" : "hover:bg-rp-surface/60"}`}
                            data-testid={`search-result-${h.path.replace(/[^a-z0-9]/gi, "-")}`}
                          >
                            <span className="flex-1 min-w-0">
                              <span className={`block text-sm font-semibold truncate ${active ? "text-orange-300" : "text-rp-text"}`}>
                                {h.title}
                              </span>
                              {h.subtitle && (
                                <span className="block text-xs text-rp-textDim truncate">{h.subtitle}</span>
                              )}
                              <span className="block text-[10px] text-rp-textFaint font-mono mt-0.5">{h.path}</span>
                            </span>
                            {active && <CornerDownLeft className="h-3.5 w-3.5 text-orange-300 mt-1 shrink-0" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>

            <div className="border-t border-rp-border px-4 py-2 flex items-center gap-4 text-[10px] font-mono text-rp-textFaint">
              <span className="inline-flex items-center gap-1"><ArrowUp className="h-3 w-3" /><ArrowDown className="h-3 w-3" /> navigate</span>
              <span className="inline-flex items-center gap-1"><CornerDownLeft className="h-3 w-3" /> open</span>
              <span>esc to close</span>
              <span className="ml-auto">{data.total ? `${data.total} results` : ""}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
